// Pieces of the staff Lead Inspector with no JSX: endpoints, statuses,
// labels, request params, the URL's filters and the ET formatters. In a `.js`
// file so `react-refresh/only-export-components` stays quiet, same as
// components/staff-leads/model.js.
//
// Shape of what the two endpoints return: InspectorListOut and
// InspectorDetailOut in the backend's leads/inspector_api.py, computed by
// leads/lead_inspector.py. Every code below (statuses, outcomes, steps,
// reasons) belongs to the backend; this file only decides how a code reads
// on screen. Buyer names are never produced here: the only names on screen
// are the ones a response carries.
import toast from 'react-hot-toast'
import { FALLBACK_TIMEZONE, outcomeLabel, outcomeTone } from '../staff-leads/model'
import { parseUrlRange, rangeParams, rangeSearch } from '../../utils/dateRange'

export { FALLBACK_TIMEZONE, outcomeLabel, outcomeTone }

export const LIST_ENDPOINT = '/admin/leads/inspector'
export const detailEndpoint = (id) => `/admin/leads/${id}/inspect`

// A range is a preset id below or a custom `{ from, to }` (utils/dateRange).
export const DEFAULT_RANGE = 'today'

// InspectorRange in leads/lead_inspector.py, less `custom`, which
// RangeSelect's Custom button sends with date_from / date_to.
export const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
]

// The only name a hidden buyer ever has on this screen.
export const OTHER_BUYER_LABEL = 'Other buyer'

const n = (v) => Number(v || 0)

// ── statuses ────────────────────────────────────────────────────────────

const TONE = {
  green: { fg: 'var(--green)', bg: 'var(--green-bg)' },
  amber: { fg: 'var(--amber)', bg: 'var(--amber-bg)' },
  red: { fg: 'var(--red)', bg: 'var(--red-bg)' },
  blue: { fg: 'var(--blue)', bg: 'var(--blue-bg)' },
  purple: { fg: 'var(--purple)', bg: 'var(--purple-bg)' },
  muted: { fg: 'var(--text-secondary)', bg: 'var(--bg-raised)' },
}

// InspectorStatus: the dashboard's EffectiveStatus plus `blocked` (intake
// rejections, which never became leads and are not in `all`). `new` is
// routing that has not finished; its chip only shows when there is some.
export const STATUSES = [
  { id: 'all', label: 'All' },
  { id: 'sold', label: 'Sold', tone: 'green' },
  { id: 'accepted_not_sold', label: 'Accepted, not sold', tone: 'amber' },
  { id: 'rejected', label: 'Rejected', tone: 'red' },
  { id: 'new', label: 'Still routing', tone: 'blue', onlyWhenSome: true },
  { id: 'blocked', label: 'Blocked at intake', tone: 'purple' },
]
export const DEFAULT_STATUS = 'all'

const humanize = (code) => String(code).replace(/_/g, ' ')
const sentence = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

export function statusLabel(status) {
  return STATUSES.find((s) => s.id === status)?.label || sentence(humanize(status || 'unknown'))
}
export const statusTone = (status) => TONE[STATUSES.find((s) => s.id === status)?.tone || 'muted']

// The chips on screen for a set of counts: Still routing only when there is
// some, or when it is the one selected (a shared link must not lose its
// selection).
export const visibleStatuses = (counts, active) =>
  STATUSES.filter((s) => !s.onlyWhenSome || n(counts?.[s.id]) > 0 || s.id === active)

// ── buyer responses ─────────────────────────────────────────────────────

// A buyer's reason is a routing code (`timeout`, `http_502`, an exclusion
// rule) or the buyer's own words. Codes read as words; anything that is
// already words is shown as the buyer wrote it.
const BUYER_REASON_LABELS = {
  timeout: 'Timed out',
  criteria_unmet: 'Buyer criteria not met',
  inactive_buyer: 'Buyer inactive',
  duplicate: 'Duplicate for this buyer',
  missing_bid_id: 'Bid had no bid ID',
  mapped_to_zero: 'Price mapped to zero',
  ask_price_unmapped: 'Ask price not mapped',
  invalid_ask_price: 'Invalid ask price',
}

const HTTP_REASON = /^http_(\d{3})$/
const CODE = /^[a-z0-9]+(_[a-z0-9]+)*$/

export function buyerReasonLabel(code) {
  if (code === null || code === undefined || code === '') return null
  const text = String(code)
  const http = HTTP_REASON.exec(text)
  if (http) return `Buyer answered HTTP ${http[1]}`
  if (BUYER_REASON_LABELS[text]) return BUYER_REASON_LABELS[text]
  return CODE.test(text) ? sentence(humanize(text)) : text
}

// ── timeline ────────────────────────────────────────────────────────────

// _timeline() steps in leads/lead_inspector.py.
const STEP_LABELS = {
  received: 'Lead received',
  validation: 'Validation',
  duplicate_check: 'Duplicate check',
  compliance: 'Compliance',
  scoring: 'Scoring',
  routing_started: 'Routing started',
  ping: 'Buyer ping',
  buyer_excluded: 'Buyer excluded',
  below_floor: 'Bid below floor',
  post_failed: 'Post failed',
  post_confirmed: 'Post confirmed',
  counter_offer: 'Counter offer',
  routing_result: 'Routing result',
  routing_error: 'Routing error',
  redirect: 'Redirect',
  email: 'Email',
  postback: 'Postback',
  result: 'Result',
}

export const stepLabel = (step) => STEP_LABELS[step] || sentence(humanize(step || 'step'))

const GOOD = new Set(['ok', 'passed', 'scored', 'sold', 'sent', 'delivered', 'bid', 'attempted', 'won'])
const BAD = new Set(['error', 'failed', 'bounced', 'complained', 'rejected'])
const WARN = new Set(['no_bid', 'skipped', 'delayed', 'timeout', 'accepted_not_sold', 'not_sold'])

export function stepTone(outcome) {
  const o = String(outcome || '').toLowerCase()
  if (GOOD.has(o)) return TONE.green
  if (BAD.has(o)) return TONE.red
  if (WARN.has(o)) return TONE.amber
  return TONE.muted
}

export const outcomeText = (outcome) => (outcome ? sentence(humanize(String(outcome).toLowerCase())) : null)

// The inferred `result` step (a lead with no routing outcome recorded): its
// outcome is the lead's effective status, and a sale names the winner the
// backend sent - never one this screen looked up.
const RESULT_TEXT = { accepted_not_sold: 'Not sold', rejected: 'Rejected' }

export function resultText(step) {
  if (step.outcome === 'sold') return step.buyer ? `Sold to ${step.buyer}` : 'Sold'
  return RESULT_TEXT[step.outcome] || outcomeText(step.outcome)
}

// ── outbound ────────────────────────────────────────────────────────────

// _postback()'s reasons when no postback was recorded.
const POSTBACK_REASONS = {
  still_routing: 'Routing has not finished',
  no_config: 'Partner has no postback configured',
  config_inactive: 'Partner postback is inactive',
  trigger_mismatch: 'Lead status does not trigger the partner postback',
  not_recorded: 'Not recorded',
}

export const postbackReasonLabel = (code) => (code ? POSTBACK_REASONS[code] || buyerReasonLabel(code) : null)

const SOURCE_LABELS = {
  intake: 'Intake', routing: 'Routing', email: 'Email', postback: 'Postback',
  compliance: 'Compliance', payout: 'Payout', financial: 'Financial', staff: 'Staff',
}
export const sourceLabel = (source) => SOURCE_LABELS[source] || sentence(humanize(source || ''))

// ── request params ──────────────────────────────────────────────────────

const csv = (values) => (values && values.length ? values.join(',') : undefined)
const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''))

// One page of the list. `range` is sent with a search too: the backend
// ignores it there, and leaving it in keeps the request equal to the URL.
export const listParams = ({ q, status, origins, buyers, range, page }) =>
  clean({ q: q || undefined, status, origins: csv(origins), buyers: csv(buyers), ...rangeParams(range), page })

// ── filters in the URL ──────────────────────────────────────────────────

const listFrom = (value) => (value ? String(value).split(',').map((v) => v.trim()).filter(Boolean) : [])

// Status, range and page are checked against what this screen knows.
// Buyers and origins come out as candidates only: allowedFilters() checks
// them against the first response's filter_options before any request
// carries them.
export function parseUrlFilters(searchParams) {
  const status = searchParams.get('status')
  const page = Number(searchParams.get('page'))
  return {
    q: (searchParams.get('q') || '').trim(),
    status: STATUSES.some((s) => s.id === status) ? status : DEFAULT_STATUS,
    range: parseUrlRange(searchParams, RANGE_PRESETS, DEFAULT_RANGE),
    page: Number.isInteger(page) && page > 1 ? page : 1,
    buyers: listFrom(searchParams.get('buyers')),
    origins: listFrom(searchParams.get('origins')),
  }
}

// Only what filter_options offers survives. A hidden buyer is never in
// filter_options.buyers, so its id is dropped here: never sent, never a
// chip, never written back into the URL.
export function allowedFilters({ buyers = [], origins = [] }, options) {
  const buyerIds = new Set((options?.buyers || []).map((b) => String(b.id)))
  const originNames = new Set(options?.origins || [])
  return {
    buyers: [...new Set(buyers.map(String))].filter((id) => buyerIds.has(id)),
    origins: [...new Set(origins)].filter((o) => originNames.has(o)),
  }
}

// The query string for a state, defaults left out.
export const filtersToSearch = ({ q, status, origins, buyers, range, page }) =>
  clean({
    q: q || undefined,
    status: status !== DEFAULT_STATUS ? status : undefined,
    origins: csv(origins),
    buyers: csv(buyers),
    ...rangeSearch(range, DEFAULT_RANGE),
    page: page > 1 ? String(page) : undefined,
  })

// ── formatting (America/New_York) ───────────────────────────────────────

const formatters = new Map()
function formatter(timeZone, options) {
  const key = `${timeZone}|${JSON.stringify(options)}`
  if (!formatters.has(key)) formatters.set(key, new Intl.DateTimeFormat('en-US', { timeZone, ...options }))
  return formatters.get(key)
}

const parse = (value) => {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

// "Oct 9, 14:03:07" - a row's received time.
export function fmtReceived(value, timeZone = FALLBACK_TIMEZONE) {
  const d = parse(value)
  if (!d) return '—'
  return formatter(timeZone, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(d)
}

// "Oct 9, 2026, 14:03:07" - a date and time with the year, for the header.
export function fmtFull(value, timeZone = FALLBACK_TIMEZONE) {
  const d = parse(value)
  if (!d) return '—'
  return formatter(timeZone, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(d)
}

// "14:03:07.123" - the timeline's clock, to the millisecond.
export function fmtClockMs(value, timeZone = FALLBACK_TIMEZONE) {
  const d = parse(value)
  if (!d) return '—'
  const hms = formatter(timeZone, { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(d)
  return `${hms}.${String(d.getUTCMilliseconds()).padStart(3, '0')}`
}

// "Oct 9" - the day a timeline step falls on, shown when it changes.
export function fmtDay(value, timeZone = FALLBACK_TIMEZONE) {
  const d = parse(value)
  if (!d) return ''
  return formatter(timeZone, { month: 'short', day: 'numeric' }).format(d)
}

export const tzLabel = (timeZone) => (timeZone === FALLBACK_TIMEZONE ? 'ET' : timeZone)

export const fmtLatency = (ms) => (ms === null || ms === undefined ? null : `${n(ms).toLocaleString('en-US')} ms`)

export const fmtJson = (value) => JSON.stringify(value, null, 2)

// A submitted value as text: nested values stay as submitted, as JSON.
export function fmtValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// Copies text, saying so either way.
export async function copyText(text, what = 'Copied') {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(what)
  } catch {
    toast.error('Could not copy - your browser blocked the clipboard')
  }
}

export const LEAD_INSPECTOR_RESPONSIVE_CSS = `
/* No backticks in these comments: this lives inside a JS template literal. */
.li-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.li-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.li-table th { text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); padding: 10px 14px; border-bottom: 1px solid var(--border); white-space: nowrap; }
.li-table td { padding: 10px 14px; border-bottom: 1px solid var(--border); color: var(--text-primary); vertical-align: top; }
.li-table tr:last-child td { border-bottom: none; }
.li-table .li-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.li-row-link { cursor: pointer; transition: background .12s; }
.li-row-link:hover, .li-row-link:focus-within { background: var(--bg-raised); }
.li-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.li-chip { display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid var(--border); background: transparent; color: var(--text-secondary); transition: all .12s; }
.li-chip[aria-pressed="true"] { background: var(--accent-soft); color: var(--accent); border-color: var(--accent); }
.li-chip .li-count { font-variant-numeric: tabular-nums; color: var(--text-muted); font-weight: 500; }
.li-chip[aria-pressed="true"] .li-count { color: var(--accent); }
.li-chip:focus-visible, .li-buyer:focus-visible, .li-toggle:focus-visible, .li-row-link:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.li-search { display: flex; gap: 8px; }
.li-search input { flex: 1; min-width: 0; padding: 12px 14px 12px 40px; font-size: 15px; border-radius: var(--radius); border: 1px solid var(--border-strong); background: var(--bg-surface); color: var(--text-primary); }
.li-search input:focus { outline: 2px solid var(--accent); outline-offset: 1px; border-color: var(--accent); }
.li-facts { display: flex; flex-wrap: wrap; gap: 8px; }
.li-fact { display: inline-flex; flex-direction: column; gap: 2px; padding: 6px 10px; border-radius: 8px; background: var(--bg-raised); border: 1px solid var(--border); min-width: 0; max-width: 100%; }
.li-fact-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: var(--text-muted); }
.li-fact-value { font-size: 13px; font-weight: 600; color: var(--text-primary); overflow-wrap: anywhere; }
.li-grid { display: grid; gap: 16px; grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); align-items: start; }
.li-kv { display: grid; grid-template-columns: minmax(120px, 38%) minmax(0, 1fr); font-size: 13px; }
.li-kv dt { color: var(--text-muted); padding: 7px 12px 7px 0; border-bottom: 1px solid var(--border); overflow-wrap: anywhere; }
.li-kv dd { margin: 0; padding: 7px 0; border-bottom: 1px solid var(--border); color: var(--text-primary); overflow-wrap: anywhere; }
.li-buyer { display: block; width: 100%; text-align: left; border: 1px solid var(--border); background: var(--bg-surface); border-radius: var(--radius); padding: 12px 14px; color: var(--text-primary); font: inherit; }
button.li-buyer { cursor: pointer; transition: border-color .12s; }
button.li-buyer:hover { border-color: var(--border-strong); }
.li-buyer[aria-expanded="true"] { border-color: var(--accent); }
.li-json { margin: 0; padding: 12px; max-height: 360px; overflow: auto; font-size: 12px; line-height: 1.5; border-radius: 8px; background: var(--bg-raised); border: 1px solid var(--border); color: var(--text-primary); white-space: pre-wrap; overflow-wrap: anywhere; }
.li-toggle { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; padding: 0; font: inherit; font-size: 13px; font-weight: 600; color: var(--text-primary); cursor: pointer; }
.li-step-inferred { color: var(--text-muted); }
.li-spin { animation: li-spin 0.9s linear infinite; }
@keyframes li-spin { to { transform: rotate(360deg); } }
.li-skeleton { background: var(--bg-raised); border-radius: 6px; animation: li-pulse 1.2s ease-in-out infinite; }
@keyframes li-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
@media (prefers-reduced-motion: reduce) { .li-spin, .li-skeleton { animation: none; } }
@media (max-width: 1100px) {
  .li-grid { grid-template-columns: 1fr !important; }
}
@media (max-width: 768px) {
  .li-header { flex-direction: column !important; align-items: stretch !important; }
  .li-header-actions { justify-content: flex-start !important; flex-wrap: wrap; }
  .li-search input { font-size: 16px !important; }
  .li-kv { grid-template-columns: 1fr; }
  .li-kv dt { border-bottom: none; padding-bottom: 0; font-size: 11px; }
  .li-hide-sm { display: none !important; }
}
`
